import dayjs from 'dayjs';
import lib from '../../../assets/utils/lib';

export const FACILITY_ENCOUNTER_SUMMARY_EXPORT_TYPE = 'claims.facility_encounter_summary';

/** Default scope: current calendar month (aligned with claims month drill-down). */
export function getDefaultFilterValues() {
  return {
    period_type: 'month',
    single_month: dayjs(),
    date_type: 'checkin_date',
    source: 'all',
    encounter_status: 'all',
    output_format: 'csv',
  };
}

export const OUTPUT_FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV (.csv)' },
  { value: 'xlsx', label: 'Excel (.xlsx)' },
  { value: 'pdf', label: 'PDF (.pdf)' },
];

export const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  { value: 'manual', label: 'Manual' },
  { value: 'direct', label: 'Direct' },
];

export const ENCOUNTER_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'checked_out', label: 'Checked out' },
];

export const PERIOD_TYPE_OPTIONS = [
  { value: 'month', label: 'Single month' },
  { value: 'date_range', label: 'Date range' },
  { value: 'month_range', label: 'Month range' },
  { value: 'all_time', label: 'All time' },
];

export function validateFacilityEncounterSummaryFilters(values) {
  const periodType = values?.period_type || 'month';

  if (periodType === 'month') {
    if (!values?.single_month?.isValid?.()) {
      throw new Error('Select a month for the report period.');
    }
    return;
  }

  if (periodType === 'date_range') {
    if (!values?.date_range?.[0]?.isValid?.() || !values?.date_range?.[1]?.isValid?.()) {
      throw new Error('Select a start and end date for the report period.');
    }
    return;
  }

  if (periodType === 'month_range') {
    if (!values?.month_range?.[0]?.isValid?.() || !values?.month_range?.[1]?.isValid?.()) {
      throw new Error('Select a start and end month (YYYY-MM) for the report period.');
    }
    return;
  }

  if (periodType === 'all_time') {
    return;
  }

  throw new Error('Invalid period type.');
}

export function buildFacilityEncounterSummaryParams(values, extras = {}) {
  validateFacilityEncounterSummaryFilters(values);
  const periodType = values.period_type || 'month';
  const params = {
    period_type: periodType,
    date_type: values.date_type || 'checkin_date',
    source: values.source || 'all',
    encounter_status: values.encounter_status || 'all',
    sort_by: extras.sortBy || values.sort_by || 'total_encounters',
    sort_order: extras.sortOrder || values.sort_order || 'desc',
  };

  if (periodType === 'date_range') {
    params.date_from = values.date_range?.[0]?.format('YYYY-MM-DD');
    params.date_to = values.date_range?.[1]?.format('YYYY-MM-DD');
  } else if (periodType === 'month') {
    params.month = values.single_month?.format('YYYY-MM');
  } else if (periodType === 'month_range') {
    params.month_from = values.month_range?.[0]?.format('YYYY-MM');
    params.month_to = values.month_range?.[1]?.format('YYYY-MM');
  }

  if (values.search?.trim()) params.search = values.search.trim();
  if (values.state) params.state = values.state;
  if (values.has_escalation === true) params.has_escalation = true;
  else if (values.has_escalation === false) params.has_escalation = false;
  if (values.organization_id != null && values.organization_id !== '') {
    params.organization_id = values.organization_id;
  }
  if (values.output_format) params.output_format = values.output_format;
  if (extras.page != null) params.page = extras.page;
  if (extras.pageSize != null) params.pageSize = extras.pageSize;

  return params;
}

export async function previewFacilityEncounterSummary(params) {
  const response = await lib.previewFacilityEncounterSummary(params);
  if (response?.data?.error) {
    throw new Error(response.data.message || 'Preview failed');
  }
  return response?.data?.data;
}

export async function queueFacilityEncounterSummaryExport(params) {
  const response = await lib.createClaimsExportJob(FACILITY_ENCOUNTER_SUMMARY_EXPORT_TYPE, params);
  if (response?.data?.error) {
    throw new Error(response.data.message || 'Unable to queue export job');
  }
  return response;
}

export async function downloadFacilityEncounterSummarySync(params) {
  return lib.downloadFacilityEncounterSummarySync(params);
}

export function parseListResponse(body) {
  const root = body?.data ?? body;
  if (!root || typeof root !== 'object' || Array.isArray(root)) {
    return {
      scope: null,
      facilities: [],
      aggregates: null,
      pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 },
      search: null,
    };
  }
  return {
    scope: root.scope || null,
    facilities: Array.isArray(root.facilities) ? root.facilities : [],
    aggregates: root.aggregates || null,
    pagination: root.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 0 },
    search: root.search || null,
  };
}

/** Normalize myApi / axios response and throw on application errors. */
export function assertFacilityEncounterSummaryResponse(resp) {
  const envelope = lib.parseApiEnvelope(resp);
  if (!envelope.ok) {
    throw new Error(envelope.message || 'Failed to load facility summary');
  }
  return envelope.body;
}
